import React, { useState } from 'react';

interface PackageFeature {
  text: string;
  isBonus?: boolean;
}

interface MarketingPackage {
  name: string;
  price: string;
  features: PackageFeature[];
  variant: 'light' | 'purple' | 'gradient' | 'dark';
  isBestValue?: boolean;
}

const starterPackages: MarketingPackage[] = [
  {
    name: 'Reach',
    price: '3,000 kr.',
    features: [
      { text: 'Featured on platform for 1 week' },
      { text: '1 newsletter spot' },
    ],
    variant: 'light',
  },
  {
    name: 'Impact',
    price: '5,000 kr.',
    features: [
      { text: 'Featured on platform for 2 weeks' },
      { text: '2 newsletter spots' },
      { text: '1 push notification' },
    ],
    variant: 'purple',
  },
];

const premiumPackages: MarketingPackage[] = [
  {
    name: 'Dominate',
    price: '8,000 kr.',
    features: [
      { text: 'Featured on platform for 4 weeks' },
      { text: '3 newsletter spots' },
      { text: '1 newsletter header feature' },
      { text: '1 push notification' },
    ],
    variant: 'gradient',
  },
  {
    name: 'Conquer',
    price: '25,000 kr.',
    features: [
      { text: 'Everything in Dominate' },
      { text: 'In-platform game feature', isBonus: true },
      { text: 'Lilla Deal exclusive', isBonus: true },
    ],
    variant: 'dark',
    isBestValue: true,
  },
];

const PackageCard: React.FC<{ pkg: MarketingPackage; onCopyEmail: () => void; copied: boolean }> = ({ pkg, onCopyEmail, copied }) => {
  const variantStyles = {
    light: 'bg-gray-50 border-gray-200',
    purple: 'bg-primary-100 border-primary-300',
    gradient: 'bg-gradient-to-br from-primary-600 to-primary-800 text-white border-primary-600',
    dark: 'bg-primary-950 text-white border-yellow-400 border-2',
  };

  const titleStyles = {
    light: 'text-primary-700',
    purple: 'text-primary-700',
    gradient: 'text-yellow-300',
    dark: 'text-yellow-400',
  };

  const priceStyles = {
    light: 'text-gray-900',
    purple: 'text-gray-900',
    gradient: 'text-white',
    dark: 'text-white',
  };

  const checkStyles = {
    light: 'text-primary-600',
    purple: 'text-primary-600',
    gradient: 'text-yellow-300',
    dark: 'text-white',
  };

  const starStyles = {
    light: 'text-yellow-500',
    purple: 'text-yellow-500',
    gradient: 'text-yellow-300',
    dark: 'text-yellow-400',
  };

  return (
    <div
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) return;
        window.location.href = 'mailto:partner@oisterbonus.dk';
      }}
      className={`relative rounded-2xl border p-6 flex flex-col group cursor-pointer ${variantStyles[pkg.variant]}`}
    >
      {pkg.isBestValue && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-primary-950 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide z-10">
          Best Value
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className={`text-xl font-semibold mb-2 ${titleStyles[pkg.variant]}`}>
          {pkg.name}
        </h3>
        <p className={`text-3xl font-bold ${priceStyles[pkg.variant]}`}>
          {pkg.price}
        </p>
      </div>

      <div className="border-t border-current opacity-20 mb-6" />

      <ul className="space-y-3 flex-1">
        {pkg.features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2">
            {feature.isBonus ? (
              <span className={`text-lg leading-none ${starStyles[pkg.variant]}`}>★</span>
            ) : (
              <svg
                className={`w-5 h-5 flex-shrink-0 ${checkStyles[pkg.variant]}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            <span className={feature.isBonus ? starStyles[pkg.variant] : ''}>
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {/* Hover Overlay */}
      <div className="absolute inset-0 rounded-2xl bg-primary-950 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-6">
        <p className="text-white text-center">
          Contact{' '}
          <a
            href="mailto:partner@oisterbonus.dk"
            className="text-yellow-300 underline hover:text-yellow-200 transition-colors"
          >
            partner@oisterbonus.dk
          </a>{' '}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopyEmail();
            }}
            className="inline-flex items-center gap-1 text-primary-300 hover:text-white transition-colors"
            title="Copy email to clipboard"
          >
            (
            {copied ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
            {copied ? 'copied!' : 'copy'}
            )
          </button>{' '}
          to discuss which package is right for your business
        </p>
      </div>
    </div>
  );
};

const UpgradeMarketingPackage: React.FC = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('partner@oisterbonus.dk');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-12">
        <h1 className="text-3xl font-bold mb-2">Marketing Packages</h1>
        <p className="text-gray-600 text-lg">Get your brand in front of our users</p>
        <div className="mt-4 inline-block bg-yellow-400 text-primary-950 font-semibold px-4 py-2 rounded-lg">
          60,000+ users and growing
        </div>
      </div>

      {/* Starter Packages */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-primary-700 mb-6">Starter Packages</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {starterPackages.map((pkg) => (
            <PackageCard key={pkg.name} pkg={pkg} onCopyEmail={handleCopyEmail} copied={copied} />
          ))}
        </div>
      </section>

      {/* Premium Packages */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-primary-700 mb-6">Premium Packages</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {premiumPackages.map((pkg) => (
            <PackageCard key={pkg.name} pkg={pkg} onCopyEmail={handleCopyEmail} copied={copied} />
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) return;
          window.location.href = 'mailto:partner@oisterbonus.dk';
        }}
        className="bg-[#6B21A8] rounded-2xl p-8 text-center text-white cursor-pointer hover:bg-primary-950 transition-all duration-300"
      >
        <h2 className="text-2xl font-bold mb-6 italic">Let's grow together</h2>
        <p className="text-primary-200">
          Contact{' '}
          <a
            href="mailto:partner@oisterbonus.dk"
            className="text-white underline hover:text-yellow-300 transition-colors"
          >
            partner@oisterbonus.dk
          </a>{' '}
          <button
            onClick={handleCopyEmail}
            className="inline-flex items-center gap-1 text-primary-300 hover:text-white transition-colors"
            title="Copy email to clipboard"
          >
            (
            {copied ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
            {copied ? 'copied!' : 'copy'}
            )
          </button>{' '}
          to discuss which package is right for your business
        </p>
      </section>
    </div>
  );
};

export default UpgradeMarketingPackage;
